import React from "react";
import PublicNavbar from "../../components/public/PublicNavbar";
import { Link } from "react-router-dom";
import { FaEnvelope, FaWhatsapp, FaShieldAlt, FaLightbulb, FaBullseye } from "react-icons/fa";

const About = () => {
    return (
        <div className="min-h-screen bg-[#FFFFFF] text-[#263746] font-sans">
            <PublicNavbar />

            {/* Header Banner */}
            <div className="bg-[#F7F5EF] border-b border-[#E5E7EB] py-14 px-6 text-center">
                <div className="max-w-4xl mx-auto flex flex-col items-center">
                    <img
                        src="/logo.png"
                        alt="LUMIGO Projection Solutions"
                        className="h-12 sm:h-14 w-auto object-contain mb-4 drop-shadow-xs"
                    />
                    <span className="text-[#E3B629] text-xs font-black tracking-[0.25em] uppercase block mb-2">
                        Precision Optical Equipment
                    </span>
                    <h1 className="text-3xl sm:text-5xl font-extrabold text-[#263746] leading-tight">
                        About LUMIGO
                    </h1>
                    <p className="text-[#687482] text-sm sm:text-base mt-4 max-w-2xl mx-auto leading-relaxed">
                        Dedicated provider of commercial gobo projection lights, custom laser-etched dichroic glass gobos, and architectural illumination solutions.
                    </p>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-5xl mx-auto px-6 py-16 space-y-16">
                {/* Introduction Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
                    <div className="space-y-4">
                        <span className="text-[#E3B629] text-xs font-black tracking-widest uppercase">
                            Who We Are • By Fourlifters
                        </span>
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-[#263746] leading-snug">
                            Specialized Projection Engineering for Demanding Commercial Environments
                        </h2>
                        <p className="text-[#687482] text-xs sm:text-sm leading-relaxed">
                            LUMIGO is an advanced commercial projection solutions brand by <strong>Fourlifters Private Limited</strong>, based in MIDC Bhosari, Pune. We focus strictly on high-performance optical gobo projection hardware and custom projection solutions. We supply industrial plants, corporate facilities, retail storefronts, and event spaces with projection systems that deliver intense brightness, crisp focus, and continuous durability.
                        </p>
                        <p className="text-[#687482] text-xs sm:text-sm leading-relaxed">
                            Rather than offering generic consumer lighting, we curate verified projector models across outdoor IP65 weatherproof, indoor high-lumen, and dynamic multi-logo rotation series to meet precise architectural and safety requirements.
                        </p>
                    </div>

                    <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-[#E5E7EB] shadow-md bg-[#F7F5EF]">
                        <img
                            src="/assets/lumigo/projector-2.jpg"
                            alt="LUMIGO Gobo Projector Hardware"
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.onerror = null; e.target.src = "/assets/lumigo/projector-1.jpg"; }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                        <div className="absolute bottom-4 left-4 right-4 text-xs text-white font-semibold">
                            DXAX-150T Outdoor Gobo Projector Reference Architecture
                        </div>
                    </div>
                </div>

                {/* Our Focus Pillars */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
                    <div className="bg-[#FFFFFF] border border-[#E5E7EB] p-6 rounded-2xl space-y-3 shadow-sm hover:border-[#E3B629] transition">
                        <div className="text-2xl text-[#E3B629]">
                            <FaShieldAlt />
                        </div>
                        <h3 className="text-lg font-bold text-[#263746]">Rugged Durability</h3>
                        <p className="text-[#687482] text-xs leading-relaxed">
                            Engineered with heavy-gauge aluminium alloy enclosures, IP65 ingress protection, and advanced thermal management to run reliably in continuous industrial shifts.
                        </p>
                    </div>

                    <div className="bg-[#FFFFFF] border border-[#E5E7EB] p-6 rounded-2xl space-y-3 shadow-sm hover:border-[#E3B629] transition">
                        <div className="text-2xl text-[#E3B629]">
                            <FaLightbulb />
                        </div>
                        <h3 className="text-lg font-bold text-[#263746]">OSRAM Optical Engines</h3>
                        <p className="text-[#687482] text-xs leading-relaxed">
                            Equipped with high-output OSRAM LED sources and precision optical lenses to maximize luminous flux and maintain edge-to-edge optical sharpness.
                        </p>
                    </div>

                    <div className="bg-[#FFFFFF] border border-[#E5E7EB] p-6 rounded-2xl space-y-3 shadow-sm hover:border-[#E3B629] transition">
                        <div className="text-2xl text-[#E3B629]">
                            <FaBullseye />
                        </div>
                        <h3 className="text-lg font-bold text-[#263746]">Enquiry-Based Accuracy</h3>
                        <p className="text-[#687482] text-xs leading-relaxed">
                            We validate throw distance, target image diameter, and gobo vector artwork before order confirmation, preventing sizing errors and ensuring project success.
                        </p>
                    </div>
                </div>

                {/* Direct Business Contact Details */}
                <div className="bg-[#F7F5EF] border border-[#E5E7EB] p-8 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
                    <div>
                        <span className="text-[#E3B629] text-xs font-black uppercase tracking-wider block mb-1">
                            Business Contacts & Inquiries
                        </span>
                        <h3 className="text-xl font-bold text-[#263746]">Connect with Fourlifters / LUMIGO</h3>
                        <p className="text-[#687482] text-xs mt-1">
                            Fourlifters Private Limited • MIDC Bhosari, Pune, Maharashtra - 411026
                        </p>
                        <div className="flex flex-wrap gap-6 mt-4 text-xs font-semibold">
                            <span className="flex items-center gap-2 text-[#263746]">
                                <FaEnvelope className="text-[#E3B629]" /> fourlifters@gmail.com / sales@fourlifters.in
                            </span>
                            <a href="https://wa.me/918087680893" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#263746] hover:text-[#168B53]">
                                <FaWhatsapp className="text-[#168B53]" /> +91 8087680893
                            </a>
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <Link
                            to="/products"
                            className="bg-[#E3B629] hover:bg-[#cca021] text-[#263746] font-extrabold px-6 py-3 rounded-lg text-xs uppercase tracking-wider transition shadow-sm"
                        >
                            Browse Catalogue
                        </Link>
                        <Link
                            to="/contact"
                            className="border border-[#E5E7EB] hover:bg-[#FFFFFF] text-[#263746] font-bold px-6 py-3 rounded-lg text-xs uppercase tracking-wider transition shadow-sm"
                        >
                            Contact Us
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default About;
